import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
    DynamicHtmlDirective,
    HtmlNode,
    LoadingIndicatorHandler,
    LoadingIndicatorService,
    LoginNavigationService,
    MetaTagsService,
    NativeAppService,
    NavigationService,
    Page,
    UserService,
    WindowRef,
} from '@frontend/vanilla/core';
import { ProfilesDirective } from '@frontend/vanilla/features/content';
import { NavigationLayoutPageComponent } from '@frontend/vanilla/features/navigation-layout';
import { TrustAsHtmlPipe, TrustAsResourceUrlPipe } from '@frontend/vanilla/shared/browser';
import { RouteDataService } from '@frontend/vanilla/shared/routing';

import { PortalConstService } from '../client-config/client-constants';
import { NativeEventApiService } from '../native-app/native-event-api.service';
import { NewAccountMenuService } from '../services/account-menu-v2.service';
import { PortalNavigationService } from '../services/portal-navigation.service';
import { FilterActiveTabPipe } from './filter-active-tab.pipe';
import { PromotionsResourceService } from './promotions-resource.service';
import { CtaButton, PromotionItem, PromotionsGetAllResponse, ScrollingNavigationItem } from './promotions.models';
import { ScrollingNavigationComponent } from './scrolling-navigation.component';

@Component({
    selector: 'pt-promotions-list',
    templateUrl: 'promotions-list.component.html',
    styleUrls: ['../../styles/portal/components/mypromotions.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [
        NavigationLayoutPageComponent,
        ScrollingNavigationComponent,
        ProfilesDirective,
        DynamicHtmlDirective,
        TrustAsHtmlPipe,
        TrustAsResourceUrlPipe,
        FilterActiveTabPipe,
    ],
})
export class PromotionsListComponent implements OnInit, OnDestroy {
    initData: PromotionsGetAllResponse;
    eventClickId?: string;
    iframeUrl: string;
    showNav: boolean;
    hasIframe: boolean;
    isTopPromotion: boolean;
    selectedProduct: string;
    private loadingIndicatorHandler: LoadingIndicatorHandler;

    constructor(
        private routeData: RouteDataService,
        public user: UserService,
        private nativeApplication: NativeAppService,
        private nativeEventApi: NativeEventApiService,
        private htmlNode: HtmlNode,
        private loginNavigationService: LoginNavigationService,
        private navigationService: NavigationService,
        private page: Page,
        private metaTagsService: MetaTagsService,
        private windowRef: WindowRef,
        private promotionsService: PromotionsResourceService,
        private loadingIndicator: LoadingIndicatorService,
        private activeroute: ActivatedRoute,
        public newAccountMenuService: NewAccountMenuService,
        private portalNavigation: PortalNavigationService,
    ) {}

    ngOnInit() {
        this.initData = this.routeData.getInitData();
        if (!this.initData.viewModel.defaultTab) {
            this.loginNavigationService.goToHome();
            return;
        }
        this.showNav = !this.nativeApplication.isNativeApp;
        this.setActiveTab((this.initData.viewModel.currentTab || '').toLowerCase());
        if (this.initData.isUSNJLabel && this.activeroute.params) {
            this.activeroute.params.subscribe((params) => {
                this.setActiveTab((params['product'] || 'mypromotions').toLowerCase());
            });
        }
    }

    ngOnDestroy() {
        if (this.hasIframe) {
            this.htmlNode.setCssClass('has-iframe', false);
        }
        if (this.initData.isSportsIOsTabletNative) {
            this.metaTagsService.clearPageTags();
        }
    }

    promotionClick(item: PromotionItem) {
        const promotionItemUrl = this.portalNavigation.normalizeUrl('/promotions/') + this.selectedProduct + '/' + item.folderId;
        if (
            !this.nativeApplication.isNativeWrapper &&
            (this.nativeApplication.product === PortalConstService.SPORTSBOOK || this.nativeApplication.product === PortalConstService.BETTING)
        ) {
            this.windowRef.nativeWindow.open(this.page.lang + promotionItemUrl, '_blank');
        } else this.navigationService.goTo(promotionItemUrl);
    }

    ctaClick($event: Event, cta: CtaButton) {
        if (cta.link.indexOf('mypromotions') !== -1 && !this.user.isAuthenticated) {
            this.isTopPromotion = true;
            this.hasIframe = this.isTopPromotion && this.user.isAuthenticated;
        } else {
            // fix once casino removes ?event=promo logic from sitecore
            if (
                this.nativeApplication.product === PortalConstService.SPORTSBOOK ||
                this.nativeApplication.product === PortalConstService.BETTING ||
                this.nativeApplication.product === PortalConstService.POKER
            ) {
                this.windowRef.nativeWindow.location.href = cta.link;
            } else if (this.nativeApplication.product === PortalConstService.CASINO) {
                const eventEqualsPromoIndex = cta.link.indexOf('?event=promo');
                if (eventEqualsPromoIndex !== -1) {
                    this.nativeEventApi.clearEvent(this.eventClickId!);
                    this.eventClickId = this.nativeEventApi.raiseEvent(cta.link.substr(eventEqualsPromoIndex));
                } else {
                    this.windowRef.nativeWindow.location.href = cta.link;
                }
            } else {
                this.navigationService.goTo(cta.link, { forceReload: cta.target === '_self' });
            }
        }
        $event.preventDefault();
        $event.stopPropagation();
    }

    goToLastKnownProduct() {
        this.navigationService.goToLastKnownProduct();
    }

    goToLogin() {
        if (this.nativeApplication.isNativeApp) {
            this.loginNavigationService.goToLogin({
                appendReferrer: this.formatNativeReturnUrl(),
                forceReload: this.nativeApplication.isNativeApp,
                ignoreSpecialNativeHandling: !this.nativeApplication.isNativeWrapper,
            });
            return;
        }
        this.loginNavigationService.goToLogin({
            appendReferrer: true,
            forceReload: this.nativeApplication.isNativeApp,
            ignoreSpecialNativeHandling: !this.nativeApplication.isNativeWrapper,
        });
    }

    onTabSelected(item: ScrollingNavigationItem) {
        if (this.initData.config.loadPromotionsOnDemand && !this.isMyPromotionTabOrCurrentTab(item.id)) {
            this.selectedProduct = item.id;
            this.loadingIndicatorHandler = this.loadingIndicator.start();
            this.promotionsService.getAll(this.selectedProduct).subscribe(
                (data: any) => {
                    this.initData = data;
                    this.setActiveTab(this.selectedProduct);
                    this.loadingIndicatorHandler.done();
                },
                () => {
                    this.loadingIndicatorHandler.done();
                },
            );
        } else {
            this.selectedProduct = item.id;
            this.setActiveTab(this.selectedProduct);
        }
    }

    private setActiveTab(product: string) {
        this.selectedProduct = product;
        this.initData.viewModel.tabItems.map((i) => {
            i.active = i.id.toLowerCase() === product;
        });
        this.isTopPromotion = product === 'mypromotions';
        this.hasIframe = this.isTopPromotion && this.user.isAuthenticated;

        this.htmlNode.setCssClass('has-iframe', this.hasIframe);

        if (this.isTopPromotion) {
            this.iframeUrl = this.initData.config.myPromotionUrl.replace('{4}', 'index');
        }
    }

    private formatNativeReturnUrl() {
        const window = this.windowRef.nativeWindow;
        let origin = window.location.origin;
        if (!origin) {
            origin = window.location.protocol + '//' + window.location.hostname + (window.location.port ? ':' + window.location.port : '');
        }
        return origin + '/' + this.nativeApplication.applicationName + 'app/promotions?product=' + this.selectedProduct;
    }

    private isMyPromotionTabOrCurrentTab(product: string) {
        return product === 'mypromotions' || this.selectedProduct === product;
    }
}
